import { create } from 'zustand';

export interface ThemeColors {
  background: string;
  foreground: string;
  card: string;
  'card-foreground': string;
  secondary: string;
  'secondary-foreground': string;
  muted: string;
  'muted-foreground': string;
  primary: string;
  'primary-hover': string;
  'primary-foreground': string;
  accent: string;
  'accent-foreground': string;
  destructive: string;
  'destructive-foreground': string;
  'destructive-border': string;
  success: string;
  warning: string;
  border: string;
  input: string;
  ring: string;
  'ring-light': string;
  'primary-90': string;
  'primary-20': string;
  'ring-70': string;
  'scrollbar-thumb': string;
  'scrollbar-thumb-hover': string;
  'banner-bg': string;
  'user-bubble-background': string;
  'user-bubble-text': string;
  'assistant-bubble-background': string;
  'assistant-bubble-text': string;
}

export interface ThemePreset {
  id: string;
  name: string;
  colors: ThemeColors;
}

export interface AppSettings {
  appearance: {
    activeTheme: string; // preset id or 'custom' or 'temp'
    customColors: Partial<ThemeColors>;
    tempTheme: ThemePreset | null;
    savedCustomThemes: ThemePreset[];
  };
  editor: {
    fontSize: number;
    codeFontFamily: string;
    codeTheme: string;
    messageDensity: 'compact' | 'comfortable' | 'spacious';
    sendOnEnter: boolean;
  };
  general: {
    displayName: string;
    defaultGatewayUrl: string;
    autoReconnect: boolean;
    maxReconnectRetries: number;
  };
}

const SETTINGS_KEY = 'openclaw.settings';

const DARK_DEFAULT_COLORS: ThemeColors = {
  background: '#0B0D14',           // Landing --bg
  foreground: '#EDF0FF',           // Landing --text
  card: '#161929',                 // Landing --card
  'card-foreground': '#A8AECB',    // Landing --text-2
  secondary: '#1C2038',            // Landing --card-2
  'secondary-foreground': '#A8AECB',
  muted: '#252A40',                // Between card-2 and border
  'muted-foreground': '#5E6480',   // Landing --muted
  primary: '#FF7B35',              // Landing --amber (THE big change)
  'primary-hover': '#FF934F',      // Lighter amber
  'primary-foreground': '#0B0D14', // Dark text on amber buttons
  accent: 'rgba(255, 123, 53, 0.07)', // Landing --amber-dim
  'accent-foreground': '#FF7B35',
  destructive: 'rgba(255, 70, 70, 0.08)', // Landing --red-dim
  'destructive-foreground': '#fecaca',
  'destructive-border': '#991b1b',
  success: '#3DFFA8',              // Landing --mint
  warning: '#FFD93D',              // Landing --yellow
  border: 'rgba(255, 255, 255, 0.06)', // Landing --border
  input: '#1C2038',                // Landing --card-2
  ring: '#FF7B35',                 // Amber ring
  'ring-light': '#FF934F',
  'primary-90': 'rgba(255, 123, 53, 0.9)',
  'primary-20': 'rgba(255, 123, 53, 0.2)',
  'ring-70': 'rgba(255, 123, 53, 0.7)',
  'scrollbar-thumb': '#252A40',
  'scrollbar-thumb-hover': '#5E6480',
  'banner-bg': 'rgba(255, 123, 53, 0.9)',
  'user-bubble-background': 'rgba(255, 123, 53, 0.12)',
  'user-bubble-text': '#EDF0FF',
  'assistant-bubble-background': '#161929',
  'assistant-bubble-text': '#A8AECB',
};

const LIGHT_COLORS: ThemeColors = {
  background: '#ffffff',
  foreground: '#1f2937',
  card: '#f3f4f6',
  'card-foreground': '#4b5563',
  secondary: '#e5e7eb',
  'secondary-foreground': '#6b7280',
  muted: '#d1d5db',
  'muted-foreground': '#9ca3af',
  primary: '#2563eb',
  'primary-hover': '#1d4ed8',
  'primary-foreground': '#ffffff',
  accent: 'rgba(59, 130, 246, 0.15)',
  'accent-foreground': '#1d4ed8',
  destructive: 'rgba(239, 68, 68, 0.1)',
  'destructive-foreground': '#dc2626',
  'destructive-border': '#fca5a5',
  success: '#16a34a',
  warning: '#d97706',
  border: '#e5e7eb',
  input: '#d1d5db',
  ring: '#2563eb',
  'ring-light': '#3b82f6',
  'primary-90': 'rgba(37, 99, 235, 0.9)',
  'primary-20': 'rgba(37, 99, 235, 0.2)',
  'ring-70': 'rgba(37, 99, 235, 0.7)',
  'scrollbar-thumb': '#d1d5db',
  'scrollbar-thumb-hover': '#9ca3af',
  'banner-bg': 'rgba(234, 179, 8, 0.9)',
  'user-bubble-background': '#1d4ed8',
  'user-bubble-text': '#ffffff',
  'assistant-bubble-background': '#e8eaed',
  'assistant-bubble-text': '#1f2937',
};

const MONOKAI_COLORS: ThemeColors = {
  background: '#272822',
  foreground: '#f8f8f2',
  card: '#2d2e27',
  'card-foreground': '#cfcfc2',
  secondary: '#3e3d32',
  'secondary-foreground': '#a59f85',
  muted: '#49483e',
  'muted-foreground': '#75715e',
  primary: '#a6e22e',
  'primary-hover': '#b6f23e',
  'primary-foreground': '#272822',
  accent: 'rgba(102, 217, 239, 0.2)',
  'accent-foreground': '#66d9ef',
  destructive: 'rgba(249, 38, 114, 0.2)',
  'destructive-foreground': '#f92672',
  'destructive-border': '#f92672',
  success: '#a6e22e',
  warning: '#e6db74',
  border: '#3e3d32',
  input: '#49483e',
  ring: '#a6e22e',
  'ring-light': '#66d9ef',
  'primary-90': 'rgba(166, 226, 46, 0.9)',
  'primary-20': 'rgba(166, 226, 46, 0.2)',
  'ring-70': 'rgba(166, 226, 46, 0.7)',
  'scrollbar-thumb': '#49483e',
  'scrollbar-thumb-hover': '#75715e',
  'banner-bg': 'rgba(230, 219, 116, 0.9)',
  'user-bubble-background': '#7aac1a',
  'user-bubble-text': '#272822',
  'assistant-bubble-background': '#353630',
  'assistant-bubble-text': '#f8f8f2',
};

const NORD_COLORS: ThemeColors = {
  background: '#2e3440',
  foreground: '#eceff4',
  card: '#3b4252',
  'card-foreground': '#d8dee9',
  secondary: '#434c5e',
  'secondary-foreground': '#81a1c1',
  muted: '#4c566a',
  'muted-foreground': '#616e88',
  primary: '#88c0d0',
  'primary-hover': '#8fbcbb',
  'primary-foreground': '#2e3440',
  accent: 'rgba(94, 129, 172, 0.3)',
  'accent-foreground': '#81a1c1',
  destructive: 'rgba(191, 97, 106, 0.2)',
  'destructive-foreground': '#bf616a',
  'destructive-border': '#bf616a',
  success: '#a3be8c',
  warning: '#ebcb8b',
  border: '#434c5e',
  input: '#4c566a',
  ring: '#88c0d0',
  'ring-light': '#81a1c1',
  'primary-90': 'rgba(136, 192, 208, 0.9)',
  'primary-20': 'rgba(136, 192, 208, 0.2)',
  'ring-70': 'rgba(136, 192, 208, 0.7)',
  'scrollbar-thumb': '#4c566a',
  'scrollbar-thumb-hover': '#616e88',
  'banner-bg': 'rgba(235, 203, 139, 0.9)',
  'user-bubble-background': '#6a9bab',
  'user-bubble-text': '#2e3440',
  'assistant-bubble-background': '#3b4252',
  'assistant-bubble-text': '#eceff4',
};

const DRACULA_COLORS: ThemeColors = {
  background: '#282a36',
  foreground: '#f8f8f2',
  card: '#2d2f3d',
  'card-foreground': '#bfbfbf',
  secondary: '#383a59',
  'secondary-foreground': '#6272a4',
  muted: '#44475a',
  'muted-foreground': '#6272a4',
  primary: '#bd93f9',
  'primary-hover': '#caa8fb',
  'primary-foreground': '#282a36',
  accent: 'rgba(189, 147, 249, 0.2)',
  'accent-foreground': '#bd93f9',
  destructive: 'rgba(255, 85, 85, 0.2)',
  'destructive-foreground': '#ff5555',
  'destructive-border': '#ff5555',
  success: '#50fa7b',
  warning: '#f1fa8c',
  border: '#44475a',
  input: '#44475a',
  ring: '#bd93f9',
  'ring-light': '#ff79c6',
  'primary-90': 'rgba(189, 147, 249, 0.9)',
  'primary-20': 'rgba(189, 147, 249, 0.2)',
  'ring-70': 'rgba(189, 147, 249, 0.7)',
  'scrollbar-thumb': '#44475a',
  'scrollbar-thumb-hover': '#6272a4',
  'banner-bg': 'rgba(241, 250, 140, 0.9)',
  'user-bubble-background': '#9670d6',
  'user-bubble-text': '#282a36',
  'assistant-bubble-background': '#343548',
  'assistant-bubble-text': '#f8f8f2',
};

const SOLARIZED_DARK_COLORS: ThemeColors = {
  background: '#002b36',
  foreground: '#93a1a1',
  card: '#073642',
  'card-foreground': '#839496',
  secondary: '#0a4050',
  'secondary-foreground': '#586e75',
  muted: '#1a5566',
  'muted-foreground': '#586e75',
  primary: '#268bd2',
  'primary-hover': '#2aa198',
  'primary-foreground': '#fdf6e3',
  accent: 'rgba(38, 139, 210, 0.2)',
  'accent-foreground': '#268bd2',
  destructive: 'rgba(220, 50, 47, 0.2)',
  'destructive-foreground': '#dc322f',
  'destructive-border': '#dc322f',
  success: '#859900',
  warning: '#b58900',
  border: '#073642',
  input: '#0a4050',
  ring: '#268bd2',
  'ring-light': '#2aa198',
  'primary-90': 'rgba(38, 139, 210, 0.9)',
  'primary-20': 'rgba(38, 139, 210, 0.2)',
  'ring-70': 'rgba(38, 139, 210, 0.7)',
  'scrollbar-thumb': '#0a4050',
  'scrollbar-thumb-hover': '#586e75',
  'banner-bg': 'rgba(181, 137, 0, 0.9)',
  'user-bubble-background': '#1e6fa8',
  'user-bubble-text': '#fdf6e3',
  'assistant-bubble-background': '#0a3847',
  'assistant-bubble-text': '#93a1a1',
};

const COFFEE_COLORS: ThemeColors = {
  background: '#262624',
  foreground: '#e8e4de',
  card: '#353431',
  'card-foreground': '#e8e4de',
  secondary: '#3d3c38',
  'secondary-foreground': '#d4d0c8',
  muted: '#4a4843',
  'muted-foreground': '#9b9689',
  primary: '#c4956a',
  'primary-hover': '#d4a57a',
  'primary-foreground': '#1a1918',
  accent: 'rgba(196, 149, 106, 0.2)',
  'accent-foreground': '#e8e4de',
  destructive: 'rgba(220, 90, 80, 0.25)',
  'destructive-foreground': '#f0847c',
  'destructive-border': '#8b3a35',
  success: '#7ab88c',
  warning: '#d4a55a',
  border: '#4a4843',
  input: '#3d3c38',
  ring: '#c4956a',
  'ring-light': '#8a7560',
  'primary-90': 'rgba(196, 149, 106, 0.9)',
  'primary-20': 'rgba(196, 149, 106, 0.2)',
  'ring-70': 'rgba(196, 149, 106, 0.7)',
  'scrollbar-thumb': '#4a4843',
  'scrollbar-thumb-hover': '#5a5853',
  'banner-bg': 'rgba(53, 52, 49, 0.9)',
  'user-bubble-background': '#4a3f33',
  'user-bubble-text': '#e8e4de',
  'assistant-bubble-background': '#262624',
  'assistant-bubble-text': '#e8e4de',
};

export const THEME_PRESETS: ThemePreset[] = [
  { id: 'dark-default', name: 'Dark (Default)', colors: DARK_DEFAULT_COLORS },
  { id: 'coffee', name: 'Coffee', colors: COFFEE_COLORS },
  { id: 'light', name: 'Light', colors: LIGHT_COLORS },
  { id: 'monokai', name: 'Monokai', colors: MONOKAI_COLORS },
  { id: 'nord', name: 'Nord', colors: NORD_COLORS },
  { id: 'dracula', name: 'Dracula', colors: DRACULA_COLORS },
  { id: 'solarized-dark', name: 'Solarized Dark', colors: SOLARIZED_DARK_COLORS },
];

const DEFAULT_SETTINGS: AppSettings = {
  appearance: {
    activeTheme: 'dark-default',
    customColors: {},
    tempTheme: null,
    savedCustomThemes: [],
  },
  editor: {
    fontSize: 14,
    codeFontFamily: 'ui-monospace, monospace',
    codeTheme: 'github-dark',
    messageDensity: 'comfortable',
    sendOnEnter: true,
  },
  general: {
    displayName: '',
    defaultGatewayUrl: 'wss://clawlet.robbieai.com/gateway/',
    autoReconnect: true,
    maxReconnectRetries: 10,
  },
};

function loadSettings(): AppSettings {
  if (typeof window === 'undefined') {
    return DEFAULT_SETTINGS;
  }

  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<AppSettings>;
    // Deep merge with defaults to handle new fields
    return deepMerge(DEFAULT_SETTINGS, parsed);
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function saveSettings(settings: AppSettings): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // ignore
  }
}

function deepMerge<T extends Record<string, any>>(defaults: T, overrides: Partial<T>): T {
  const result = { ...defaults };
  for (const key of Object.keys(overrides) as (keyof T)[]) {
    const val = overrides[key];
    if (
      val &&
      typeof val === 'object' &&
      !Array.isArray(val) &&
      typeof defaults[key] === 'object' &&
      !Array.isArray(defaults[key])
    ) {
      result[key] = deepMerge(defaults[key] as any, val as any);
    } else if (val !== undefined) {
      result[key] = val as T[keyof T];
    }
  }
  return result;
}

/** Apply theme colors to :root CSS custom properties */
export function applyThemeToDOM(colors: ThemeColors): void {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  for (const [key, value] of Object.entries(colors)) {
    root.style.setProperty(`--theme-${key}`, value);
  }
}

/** Apply editor CSS variables to :root */
export function applyEditorToDOM(editor: AppSettings['editor']): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.style.setProperty('--app-code-font', editor.codeFontFamily);
}

/** Get the resolved colors for the current active theme */
function resolveActiveColors(settings: AppSettings): ThemeColors {
  const { activeTheme, tempTheme, customColors, savedCustomThemes } = settings.appearance;

  if (activeTheme === 'temp' && tempTheme) {
    return { ...tempTheme.colors, ...customColors };
  }

  // Check saved custom themes
  const customTheme = savedCustomThemes.find((theme) => theme.id === activeTheme);
  if (customTheme) {
    return { ...customTheme.colors, ...customColors };
  }

  // Check presets
  const preset = THEME_PRESETS.find((theme) => theme.id === activeTheme);
  const baseColors = preset?.colors ?? DARK_DEFAULT_COLORS;
  return { ...baseColors, ...customColors };
}

interface SettingsState {
  settings: AppSettings;
  updateSettings: (patch: Partial<AppSettings>) => void;
  updateAppearance: (patch: Partial<AppSettings['appearance']>) => void;
  updateEditor: (patch: Partial<AppSettings['editor']>) => void;
  updateGeneral: (patch: Partial<AppSettings['general']>) => void;
  setActiveTheme: (themeId: string) => void;
  setTempTheme: (theme: ThemePreset | null) => void;
  saveTempThemeAs: (name: string) => void;
  updateCustomTheme: (themeId: string, updated: ThemePreset) => void;
  addCustomTheme: (theme: ThemePreset) => void;
  resetSettings: () => void;
  getActiveColors: () => ThemeColors;
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  settings: loadSettings(),

  updateSettings: (patch) => {
    set((state) => {
      const next = deepMerge(state.settings, patch as any);
      saveSettings(next);
      applyThemeToDOM(resolveActiveColors(next));
      applyEditorToDOM(next.editor);
      return { settings: next };
    });
  },

  updateAppearance: (patch) => {
    const { updateSettings } = get();
    updateSettings({ appearance: { ...get().settings.appearance, ...patch } } as AppSettings);
  },

  updateEditor: (patch) => {
    set((state) => {
      const next = {
        ...state.settings,
        editor: { ...state.settings.editor, ...patch },
      };
      saveSettings(next);
      applyEditorToDOM(next.editor);
      applyThemeToDOM(resolveActiveColors(next));
      return { settings: next };
    });
  },

  updateGeneral: (patch) => {
    const { updateSettings } = get();
    updateSettings({ general: { ...get().settings.general, ...patch } } as AppSettings);
  },

  setActiveTheme: (themeId) => {
    set((state) => {
      const next = {
        ...state.settings,
        appearance: { ...state.settings.appearance, activeTheme: themeId },
      };
      saveSettings(next);
      applyThemeToDOM(resolveActiveColors(next));
      return { settings: next };
    });
  },

  setTempTheme: (theme) => {
    set((state) => {
      const next = {
        ...state.settings,
        appearance: {
          ...state.settings.appearance,
          tempTheme: theme,
          activeTheme: theme ? 'temp' : state.settings.appearance.activeTheme,
        },
      };
      // If clearing temp, revert to dark-default if was on temp
      if (!theme && state.settings.appearance.activeTheme === 'temp') {
        next.appearance.activeTheme = 'dark-default';
      }
      saveSettings(next);
      applyThemeToDOM(resolveActiveColors(next));
      return { settings: next };
    });
  },

  saveTempThemeAs: (name) => {
    set((state) => {
      const temp = state.settings.appearance.tempTheme;
      if (!temp) return state;
      const newTheme: ThemePreset = {
        id: `custom-${Date.now()}`,
        name,
        colors: { ...temp.colors },
      };
      const next = {
        ...state.settings,
        appearance: {
          ...state.settings.appearance,
          savedCustomThemes: [...state.settings.appearance.savedCustomThemes, newTheme],
          activeTheme: newTheme.id,
          tempTheme: null,
        },
      };
      saveSettings(next);
      applyThemeToDOM(resolveActiveColors(next));
      return { settings: next };
    });
  },

  updateCustomTheme: (themeId, updated) => {
    set((state) => {
      const next = {
        ...state.settings,
        appearance: {
          ...state.settings.appearance,
          savedCustomThemes: state.settings.appearance.savedCustomThemes.map((t) =>
            t.id === themeId ? { ...t, name: updated.name, colors: { ...updated.colors } } : t,
          ),
        },
      };
      saveSettings(next);
      applyThemeToDOM(resolveActiveColors(next));
      return { settings: next };
    });
  },

  addCustomTheme: (theme) => {
    set((state) => {
      const next = {
        ...state.settings,
        appearance: {
          ...state.settings.appearance,
          savedCustomThemes: [...state.settings.appearance.savedCustomThemes, theme],
          activeTheme: theme.id,
        },
      };
      saveSettings(next);
      applyThemeToDOM(resolveActiveColors(next));
      return { settings: next };
    });
  },

  resetSettings: () => {
    set(() => {
      saveSettings(DEFAULT_SETTINGS);
      applyThemeToDOM(DARK_DEFAULT_COLORS);
      applyEditorToDOM(DEFAULT_SETTINGS.editor);
      return { settings: DEFAULT_SETTINGS };
    });
  },

  getActiveColors: () => resolveActiveColors(get().settings),
}));
